import React, { useEffect, useMemo } from "react";
import { usePage } from "@inertiajs/inertia-react";
import { useAtom } from "jotai";
import { focusAtom } from "jotai-optics";
import { reportQuestionsAtom } from "@/Stores";
import { LabeledInputCalculatable } from "@/Components/LabledInputCalculatable";
import { FormError } from "@/Components";

const Checkbox = ({
  atom,
  input,
  multi = false,
  requireValue = false,
  errors = null,
}) => {
  const [payload, setPayload] = useAtom(atom);

  const handleFormUpdate = () => {
    setPayload((prev) => ({
      ...prev,
      answers: [{ question_input_id: input.id, value: null }],
    }));
  };

  const handleFormUpdateMulti = (e) => {
    // add selected input to answers, remove if it already exists
    const { checked } = e.target;
    const updatedAnswers = [...(payload?.answers || [])];

    if (checked) {
      updatedAnswers.push({ question_input_id: input.id, value: null });
    } else {
      updatedAnswers.splice(
        updatedAnswers.findIndex((a) => a.question_input_id === input.id),
        1,
      );
    }

    setPayload((prev) => ({
      ...prev,
      answers: updatedAnswers,
    }));
  };

  const handleFormUpdateValue = (e) => {
    const { name, value } = e.target;
    const updatedAnswers = [...(payload?.answers || [])];
    const answer = updatedAnswers.find((a) => a.question_input_id === input.id);
    answer[name] = value;
    setPayload((prev) => ({
      ...prev,
      answers: updatedAnswers,
    }));
  };

  const answer = payload?.answers?.find(
    (a) => a.question_input_id === input.id,
  );

  const index = payload?.answers?.findIndex(
    (a) => a.question_input_id === input.id,
  );

  return (
    <div className="flex min-h-[4rem] flex-wrap gap-8 pt-4">
      <div className="pl-8 sm:w-80 md:max-w-[50%]">
        {multi && (
          <input
            name="value"
            type="checkbox"
            className="-ml-8 mr-3 p-2"
            id={input.id}
            checked={answer !== undefined}
            onChange={handleFormUpdateMulti}
          />
        )}

        {!multi && (
          <input
            name="value"
            type="radio"
            className="-ml-8 mr-3 p-2"
            id={input.id}
            checked={answer !== undefined}
            onChange={handleFormUpdate}
          />
        )}

        <label htmlFor={input.id}>{input.label}</label>
      </div>

      {requireValue && answer && (
        <LabeledInputCalculatable
          name="value"
          placeholder="Add amount"
          value={answer?.value || ""}
          onChange={handleFormUpdateValue}
          error={errors?.[`values.${index}.value`]}
          unit={input?.unit || ""}
        />
      )}
    </div>
  );
};

export function InputTypeSelectForm({ question, values = null }) {
  let errors = usePage().props?.errors || {};
  const errorBag = `reportQuestion.${question.id}`;

  /**
   * If the error comes from a calculator, we must reformat the errors to match the regular error bag
   */
  Object.keys(errors).forEach((key) => {
    if (key.includes(`${errorBag}.values.`)) {
      const newKey = key.split(`${errorBag}.`)[1];

      errors[errorBag] = {
        ...errors?.[errorBag],
        [newKey]: errors[key],
      };

      delete errors[key];
    }
  });

  const inputAtom = useMemo(
    () => focusAtom(reportQuestionsAtom, (optic) => optic.prop(question.id)),
    [question],
  );
  const [payload, setPayload] = useAtom(inputAtom);

  const isMultiSelect = question?.meta?.select_type === "multi";

  const selectedInput = question.inputs.find(
    (i) => i.id === payload?.answers?.[0]?.question_input_id,
  );
  const hasSelectedInputCustomEmissionAmount =
    selectedInput?.emission_factor_id &&
    selectedInput?.emission_amount === null;

  const requireValue =
    question?.meta?.require_value || hasSelectedInputCustomEmissionAmount;

  useEffect(() => {
    let defaultPayload = {
      answers: [],
    };

    if (values && values.length > 0) {
      // create payload based on values that can contain multiple answers
      defaultPayload = {
        answers: values.map((v) => ({
          question_input_id: v?.question_input_id || v.id,
          value: v?.value?.toString() || null,
        })),
      };
    } else if (!isMultiSelect) {
      defaultPayload = {
        answers: [
          {
            question_input_id:
              values?.[0]?.question_input_id || question.inputs[0].id,
            value: values?.[0]?.value?.toString() || null,
          },
        ],
      };
    }

    setPayload(defaultPayload);
  }, []);

  const handleFormUpdate = (e) => {
    const { name, value } = e.target;

    setPayload((prev) => {
      const updatedAnswers = [...(prev?.answers || [])];
      updatedAnswers[0] = {
        question_input_id: updatedAnswers[0].question_input_id,
        [name]: value,
      };

      return {
        ...prev,
        answers: updatedAnswers,
      };
    });
  };

  if (!question.inputs.length) return null;

  return (
    <>
      <form onSubmit={(e) => e.preventDefault()}>
        <span className="mb-1 block text-[14px]">
          {question.question_text}*
        </span>

        {question.inputs.map((input) => (
          <Checkbox
            key={input.id}
            atom={inputAtom}
            input={input}
            multi={isMultiSelect}
            requireValue={requireValue}
            errors={errors?.[errorBag]}
          />
        ))}

        <FormError error={errors?.[`${errorBag}.values`]} />
      </form>
    </>
  );
}
